/**
 * useHasReports — does the current user have at least one report to open?
 *
 * Drives the Reports nav item for non-authors (#239): a user who can view
 * reports but has none open to them would otherwise get a menu leading to an
 * empty "No reports yet" list. Authors (`reports.manage`) never call this —
 * they need the menu to create the first report.
 *
 * Asks GET /reports once per mount (the same list the Reports page shows, as
 * the server scopes it). The last answer is cached per user so the nav item
 * paints on first render instead of appearing after the round trip. A failed
 * request answers true: this is a UI gate only — the server decides which
 * reports anyone may open — and a network blip should not hide the menu.
 */
import { useContext, useEffect, useState } from "react";

import { AppContext } from "../AppContextProvider";
import reportsService from "../services/reportsService";

const CACHE_PREFIX = "bd.reports.hasAny.";

/** Cached answer for this user, or null. Never throws. */
export const readCached = (userOid) => {
  try {
    const raw = window.localStorage?.getItem(CACHE_PREFIX + userOid);
    return raw === "1" ? true : raw === "0" ? false : null;
  } catch {
    return null;
  }
};

/** Cache the answer. Never throws (quota, private mode). */
export const writeCached = (userOid, value) => {
  try {
    window.localStorage?.setItem(CACHE_PREFIX + userOid, value ? "1" : "0");
  } catch {
    // No cache, no harm: the next mount asks again.
  }
};

/**
 * @param {boolean} enabled  false = don't ask (an author, or no reports.view)
 * @returns {boolean} true once the user is known to have a report (or the
 *   check failed); false while unknown with no cached answer, or when they
 *   have none.
 */
export const useHasReports = (enabled) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
  const [hasReports, setHasReports] = useState(() =>
    userOid ? readCached(userOid) === true : false
  );

  useEffect(() => {
    if (!enabled || !userOid) return undefined;
    let live = true;
    (async () => {
      try {
        let tk = token;
        if (!tk || isTokenExpired(tk)) tk = await getToken();
        if (!tk) return;
        const resp = await reportsService.listReports({ token: tk, userOid });
        const rows = resp?.data?.data;
        const any = Array.isArray(rows) && rows.length > 0;
        writeCached(userOid, any);
        if (live) setHasReports(any);
      } catch {
        if (live) setHasReports(true);
      }
    })();
    return () => {
      live = false;
    };
    // Once per user per mount; a token refresh is not a reason to ask again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, userOid]);

  return hasReports;
};

export default useHasReports;
